import { createReducer } from 'reduxsauce';
import i18n from 'i18next';
import { filter, reduce, camelCase, has, map, includes } from 'lodash';
import moment from 'moment';
import * as Immutable from 'seamless-immutable';
import { createSelector } from 'reselect';
import { ApplicationTypes } from '../actions/ApplicationActions';
import { LoginTypes } from '../actions/LoginActions';
import { ConfigTypes } from '../actions/ConfigActions';
import {
  errorInActiveLocale,
  toActiveLocale,
} from '../../services/Translation';
import AppConfigs from '../../conf';
import { getLanguage } from './AppReducer';
import { getCountries } from './ConfigReducer';
import { PaymentTypes } from '../actions/PaymentActions';

const isNewApplication = formData =>
  !formData.uuid || formData.uuid.length === 0;

export const isDateField = field =>
  includes(['dob', 'passportExpireAt', 'passportDoi', 'arrivalAt'], field);

const spotCheckDate = (fieldId, value) => {
  switch (fieldId) {
    case 'dob':
    case 'passportDoi':
      if (!moment().isSameOrAfter(value, 'day')) {
        return i18n.t('mustBeOnOrBeforeToday');
      }
      break;
    case 'passportExpireAt':
      if (!moment().isBefore(value, 'day')) {
        return i18n.t('mustBeAfterToday');
      }
      break;
    case 'arrivalAt':
      if (!moment().isSameOrBefore(value, 'day')) {
        return i18n.t('mustBeOnOrAfterToday');
      }
      break;
    default:
      break;
  }
};

export const validateField = (value, validator, regex, fieldId, lang) => {
  if (!validator || process.env.REACT_APP_IGNORE_VALIDATIONS === 'true') return;

  if (
    validator.isRequired &&
    (typeof value === 'undefined' || value === null || value.length === 0)
  ) {
    return errorInActiveLocale(validator, lang);
  }
  if (
    process.env.REACT_APP_IGNORE_DATE_REGEX === 'true' &&
    isDateField(fieldId)
  ) {
    if (moment(value, 'YYYY-MM-DD', true).isValid())
      return spotCheckDate(fieldId, value);
    return errorInActiveLocale(validator, lang);
  }
  try {
    if (
      value &&
      validator.validationType === 'INPUT' &&
      regex &&
      !new RegExp(regex.regex, regex.flags).test(value)
    ) {
      return errorInActiveLocale(validator, lang);
    }
    if (isDateField(fieldId)) {
      return spotCheckDate(fieldId, value);
    }
  } catch (e) {
    console.log(`Could not validate field: ${fieldId}: `, e);
  }
};

const mapVisaPurposes = config =>
  reduce(
    config.visaPurposes || [],
    (map, purpose) => {
      map[purpose.uuid] = purpose;
      return map;
    },
    {}
  );

const mapValidations = config =>
  reduce(
    config.formValidation || [],
    (map, validator) => {
      map[camelCase(validator.fieldId)] = validator;
      return map;
    },
    {}
  );

const extractRegexes = config => {
  const result =
    config === null
      ? {}
      : reduce(
          config.formValidation || [],
          (acc, v) => {
            if (v.validationType === 'INPUT') {
              if (!v.regex || v.regex.length === 0) return acc;
              let regex = v.regex.slice(0, -1);
              if (regex.startsWith('/')) {
                regex = regex.substring(1);
              }
              let flags;
              if (regex.endsWith('/')) {
                regex = regex.substring(0, regex.length - 1);
              } else if (regex.endsWith('/i')) {
                flags = 'i';
                regex = regex.substring(0, regex.length - 2);
              }
              try {
                acc[camelCase(v.fieldId)] = { regex, flags };
              } catch (e) {
                console.log('Regex error: ', e);
              }
            }
            return acc;
          },
          {}
        );
  return result;
};

const docsUpdater = (docs, newDoc) => docs.concat([newDoc]);
const messageUpdater = (messages, newMessage) => messages.concat([newMessage]);
const applicationUpdater = (applications, newApplication) =>
  applications.concat([newApplication]);
const applicationReplacer = (applications, updated) =>
  applications.map(app => (app.uuid === updated.uuid ? updated : app));

/* ------------- Initial ChangePassword State ------------- */
export const INITIAL_STATE = Immutable({
  page: 1,
  error: '',
  fetching: false,
  applications: [],
  filters: {},
  pagination: {
    page: 0,
    size: 25,
  },
  messageSent: false,
  total: 0,
  users: [],
  formData: {},
  fullFormData: {},
  formErrors: {},
  regexes: {},
  validators: {},
  visaPurposes: {},
  requiredDocuments: [],
  optionalDocuments: [],
  eitherOrDocuments: [],
});

/* ------------- Reducers ------------- */
export const onClearCurrent = state =>
  state.merge({
    page: 1,
    formData: {},
    formErrors: {},
    error: '',
  });
export const onRequest = state => state.merge({ fetching: true });
export const onFailure = (state, { error }) =>
  state.merge({ fetching: false, error });
export const onSaveSuccess = (state, { data }) => {
  const isNew = isNewApplication(state.formData);
  let result = state.merge({
    error: '',
    fetching: false,
    formData: data,
    formErrors: {},
    page: state.page === 4 ? 4 : state.page + 1,
  });
  if (isNew) {
    result = result.update('applications', applicationUpdater, data);
  } else {
    result = result.update('applications', applicationReplacer, data);
  }
  return result;
};
export const onSubmitSuccess = (state, { data }) =>
  state.merge({ fetching: false, formData: data, error: '' });
export const onApplicationsSuccess = (state, { applications }) =>
  state.merge({ fetching: false, applications });
export const formValueChanged = (state, { id, value }) => {
  let result = state.setIn(['formData', id], value);
  let error = validateField(
    value,
    result.validators[id],
    result.regexes[id],
    id
  );
  if (id === 'arrivalAt' && !error) {
    const { passportExpireAt } = result.formData;
    if (passportExpireAt && moment(value).isAfter(moment(passportExpireAt))) {
      error = i18n.t('mustBeBeforePassportExpireDate');
    }
  }
  if (typeof error !== 'undefined') {
    result = result.setIn(['formErrors', id], error);
  } else if (has(result.formErrors, id)) {
    result = result.updateIn(['formErrors'], e => e.without(id));
  }
  if (id === 'visapurposeId') {
    const visaPurpose = result.visaPurposes[value];
    if (visaPurpose) {
      result = result.merge({
        requiredDocuments: visaPurpose.requiredDocuments || [],
        optionalDocuments: visaPurpose.optionalDocuments || [],
        eitherOrDocuments: visaPurpose.eitherOrDocuments || [],
      });
    }
  }
  return result;
};
export const onSet = (state, { application }) => {
  let result = state.merge({ fetching: false, formData: application });
  if (application.visapurposeId) {
    const visaPurpose = result.visaPurposes[application.visapurposeId];
    if (visaPurpose) {
      result = result.merge({
        requiredDocuments: visaPurpose.requiredDocuments || [],
        optionalDocuments: visaPurpose.optionalDocuments || [],
        eitherOrDocuments: visaPurpose.eitherOrDocuments || [],
      });
    }
  }
  return result;
};
export const onPrevious = state => state.merge({ page: state.page - 1 });
export const onLogout = () => INITIAL_STATE;
export const onValidateSuccess = (state, { errors }) =>
  state.merge({ formErrors: errors, fetching: false });
export const onConfigSuccess = (state, { config = null }) => {
  let result = state.merge({
    regexes: extractRegexes(config),
    validators: mapValidations(config),
    visaPurposes: mapVisaPurposes(config),
  });
  const application = result.formData;
  if (application.visapurposeId) {
    const visaPurpose = result.visaPurposes[application.visapurposeId];
    if (visaPurpose) {
      result = result.merge({
        requiredDocuments: visaPurpose.requiredDocuments || [],
        optionalDocuments: visaPurpose.optionalDocuments || [],
        eitherOrDocuments: visaPurpose.eitherOrDocuments || [],
      });
    }
  }
  return result;
};
export const onMessageSuccess = (state, { message }) => {
  let result = state.merge({ fetching: false, error: '' });
  if (result.formData.messages) {
    result = result.updateIn(['formData', 'messages'], messageUpdater, message);
  } else {
    result = result.setIn(['formData', 'messages'], [message]);
  }
  return result;
};
export const onUploadSuccess = (state, { document }) => {
  let result = state.merge({ fetching: false, error: '' });
  if (result.formData.documents) {
    result = result.updateIn(['formData', 'documents'], docsUpdater, document);
  } else {
    result = result.setIn(['formData', 'documents'], [document]);
  }
  return result;
};

const onDeleteSuccess = (state, { document }) => {
  let result = state.merge({ fetching: false, error: '' });

  const existingDocuments = state.formData.documents.filter(
    item => item.uuid !== document.uuid
  );
  result = result.setIn(['formData', 'documents'], existingDocuments);

  return result;
};

export const onFilterSet = (state, { filters }) => state.merge({ filters });
export const onUsersSuccess = (state, { users }) => state.merge({ users });
export const onCheckZeroPaymentSuccess = (state, { zeroPayment }) =>
  state.merge({ zeroPayment, fetching: false });
export const onPageSet = (state, { page }) =>
  state.setIn(['pagination', 'page'], page);
export const onPerPageSet = (state, { perPage }) =>
  state.setIn(['pagination', 'size'], perPage);
export const onTotalSet = (state, { total }) => state.merge({ total });
export const onSubmitPayment = (state, { paymentRecord }) => {
  let result = state.setIn(
    ['formData', 'paymentPaidAt'],
    paymentRecord.paymentTime
  );
  result = result.setIn(
    ['formData', 'paymentMethodName'],
    paymentRecord.paymentMethod.name
  );
  result = result.setIn(['formData', 'feeAmount'], paymentRecord.feeAmount);
  return result;
};
export const onMailSend = state => state.merge({ messageSent: false });
export const onMailSendSuccess = state => state.merge({ messageSent: true });
export const onMailSendFailure = (state, { error }) =>
  state.merge({ messageSent: false, error });
export const onClearMessageSent = state => state.merge({ messageSent: false });
/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [ApplicationTypes.SAVE_REQUEST]: onRequest,
  [ApplicationTypes.SAVE_SUCCESS]: onSaveSuccess,
  [ApplicationTypes.SAVE_FAILURE]: onFailure,
  [ApplicationTypes.SUBMIT_REQUEST]: onRequest,
  [ApplicationTypes.SUBMIT_SUCCESS]: onSubmitSuccess,
  [ApplicationTypes.SUBMIT_FAILURE]: onFailure,
  [ApplicationTypes.GET_APPLICATIONS_REQUEST]: onRequest,
  [ApplicationTypes.GET_APPLICATIONS_SUCCESS]: onApplicationsSuccess,
  [ApplicationTypes.GET_APPLICATIONS_FAILURE]: onFailure,
  [ApplicationTypes.GET_APPLICATION_REQUEST]: onRequest,
  [ApplicationTypes.GET_APPLICATION_SUCCESS]: onSet,
  [ApplicationTypes.GET_APPLICATION_FAILURE]: onFailure,
  [ApplicationTypes.GET_USERS_SUCCESS]: onUsersSuccess,
  [ApplicationTypes.GET_USERS_FAILURE]: onFailure,
  [ApplicationTypes.FORM_VALUE_CHANGED]: formValueChanged,
  [ApplicationTypes.SET_APPLICATION]: onSet,
  [ApplicationTypes.GO_TO_PREVIOUS_PAGE]: onPrevious,
  [ApplicationTypes.VALIDATE_REQUEST]: onRequest,
  [ApplicationTypes.VALIDATE_SUCCESS]: onValidateSuccess,
  [ApplicationTypes.SEND_MESSAGE_REQUEST]: onRequest,
  [ApplicationTypes.SEND_MESSAGE_SUCCESS]: onMessageSuccess,
  [ApplicationTypes.SEND_MESSAGE_FAILURE]: onFailure,
  [ApplicationTypes.SEND_MESSAGE_TO_APPLICANT_REQUEST]: onMailSend,
  [ApplicationTypes.SEND_MESSAGE_TO_APPLICANT_SUCCESS]: onMailSendSuccess,
  [ApplicationTypes.SEND_MESSAGE_TO_APPLICANT_FAILURE]: onMailSendFailure,
  [ApplicationTypes.CLEAR_MESSAGE_SENT]: onClearMessageSent,
  [ApplicationTypes.UPLOAD_REQUEST]: onRequest,
  [ApplicationTypes.UPLOAD_SUCCESS]: onUploadSuccess,
  [ApplicationTypes.UPLOAD_FAILURE]: onFailure,
  [ApplicationTypes.DELETE_DOCUMENT_REQUEST]: onRequest,
  [ApplicationTypes.DELETE_DOCUMENT_SUCCESS]: onDeleteSuccess,
  [ApplicationTypes.DELETE_DOCUMENT_FAILURE]: onFailure,
  [ApplicationTypes.CLEAR_CURRENT_APPLICATION]: onClearCurrent,
  [ApplicationTypes.SET_FILTERS]: onFilterSet,
  [ApplicationTypes.SET_PAGE]: onPageSet,
  [ApplicationTypes.SET_PER_PAGE]: onPerPageSet,
  [ApplicationTypes.SET_TOTAL]: onTotalSet,
  [ApplicationTypes.CHECK_ZERO_PAYMENT]: onRequest,
  [ApplicationTypes.SUCCESS_CHECK_ZERO_PAYMENT]: onCheckZeroPaymentSuccess,
  [ApplicationTypes.DOWNLOAD_APPLICATION_REQUEST]: onRequest,
  [ApplicationTypes.DOWNLOAD_APPLICATION_SUCCESS]: state =>
    state.merge({ fetching: false }),
  [ApplicationTypes.DOWNLOAD_APPLICATION_FAILURE]: onFailure,
  [LoginTypes.LOGOUT_SUCCESS]: onLogout,
  [ConfigTypes.GET_CONFIG_SUCCESS]: onConfigSuccess,
  [PaymentTypes.PAY_SUCCESS]: onSubmitPayment,
  [PaymentTypes.MARK_FOR_LOCAL_PAYMENT_SUCCESS]: onSet,
});

const canDisplayStatus = application =>
  application.status !== 'APPROVED' && application.status !== 'REJECTED';

/* ------------- Utils ------------- */
export const getFormError = ({ application }, fieldId) =>
  application.formErrors[fieldId];
export const isRequired = validator => !!(validator || {}).isRequired;

/* ------------- Selectors ------------- */
export const getError = ({ application }) => application.error;
export const isFetching = ({ application }) => application.fetching;
export const getApplications = ({ application }) => application.applications;
export const getData = ({ application }) => application.formData;
export const isNew = ({ application }) =>
  isNewApplication(application.formData);
export const getCurrentPage = ({ application }) => application.page;
export const getFormErrors = ({ application }) => application.formErrors;
export const getValidators = ({ application }) => application.validators;
export const getRegexes = ({ application }) => application.regexes;
// export const getRequiredDocuments = ({ application }) =>
//   application.requiredDocuments;
export const getRequiredDocuments = ({ application }) =>
  application.requiredDocuments;
export const getOptionalDocuments = ({ application }) =>
  application.optionalDocuments;
export const getEitherOrDocuments = ({ application }) =>
  application.eitherOrDocuments;
export const getFilters = ({ application }) => application.filters;
export const getPagination = ({ application }) => application.pagination;
export const getPage = ({ application }) => application.pagination.page;
export const getPerPage = ({ application }) => application.pagination.size;
export const getUsers = ({ application }) => application.users;
export const getTotal = ({ application }) => application.total;
export const getMessageSent = ({ application }) => application.messageSent;

// --- memoized

export const getMessages = createSelector(getData, data => data.messages);

export const getApps = createSelector(
  [getApplications, getLanguage],
  (applications, lang) =>
    map(applications, app => ({
      ...app,
      statusDisplay: toActiveLocale(
        AppConfigs.applicationStatuses[
          app.cfStatus || app.status || 'IN_CREATION'
        ],
        lang
      ),
    }))
);

export const getViewableApplications = createSelector(getApps, applications =>
  filter(applications, app => canDisplayStatus(app))
);
export const getApplicationId = createSelector(getData, data => data.uuid);
export const getApplicationDocuments = createSelector(
  getData,
  data => data.documents
);
export const hasApplications = createSelector(
  getViewableApplications,
  applications => applications.length > 0
);
export const getApplicationDocumentsMap = createSelector(
  getApplicationDocuments,
  documents =>
    reduce(
      documents || [],
      (map, doc) => {
        map[doc.documenttypeId] = doc;
        return map;
      },
      {}
    )
);
export const hasAllRequiredDocuments = createSelector(
  [getApplicationDocumentsMap, getRequiredDocuments, getEitherOrDocuments],
  (documentMap, requiredDocuments, eitherOrDocuments) => {
    for (let index = 0; index < requiredDocuments.length; index++) {
      const doc = requiredDocuments[index];
      if (!documentMap[doc.uuid]) return false;
    }
    for (let index = 0; index < eitherOrDocuments.length; index++) {
      const { either, or } = eitherOrDocuments[index];
      if (!(documentMap[either.uuid] || documentMap[or.uuid])) return false;
    }
    return true;
  }
);

const langKeys = {
  en: 'nameLangDefault',
  fr: 'nameAltLangOne',
  de: 'nameAltLangTwo',
};

export const getLocalization = createSelector(
  [getCountries, getLanguage],
  (countries, lang) => {
    const langKey = langKeys[lang];
    return countries.reduce((map, c) => {
      map[c.nameLangDefault] = c[langKey];
      return map;
    }, {});
  }
);



// WEBPACK FOOTER //
// ./src/state/reducers/ApplicationReducer.js